import type {
  MetaFunction,
  LoaderFunctionArgs,
  ActionFunctionArgs,
} from '@remix-run/node'
import { useState } from 'react'
import { Form, Link, useLoaderData } from '@remix-run/react'
import { redirect } from '@remix-run/node'
import {
  Check,
  Mail,
  ArrowRight,
  Zap,
  Shield,
  RefreshCw,
  BarChart3,
  Code2,
  Paperclip,
} from 'lucide-react'
import { authenticator } from '#app/modules/auth/auth.server'
import { cn } from '#app/utils/misc'
import { useTheme } from '#app/utils/hooks/use-theme.js'
import { siteConfig } from '#app/utils/constants/brand'
import { ROUTE_PATH as LOGIN_PATH } from '#app/routes/auth+/login'
import { PLANS, INTERVALS, getPlanPriceUsd } from '#app/modules/stripe/plans'
import { createSubscriptionCheckout } from '#app/modules/stripe/queries.server'
import { prisma } from '#app/utils/db.server'
import { INTENTS } from '#app/utils/constants/misc'
import { buttonVariants } from '#app/components/ui/button'
import { Button } from '#app/components/ui/button'
import { Badge } from '#app/components/ui/badge'
import { Switch } from '#app/components/ui/switch'
import { track } from '#app/utils/analytics'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '#app/components/ui/card'
import { DemoDeliveryTable } from '#app/components/home/demo-delivery-table'

import { Logo } from '#app/components/logo'
import ShadowPNG from '#public/images/shadow.png'

const HOME_TITLE = 'Inbox Bridge — Email to Webhook API & Inbound Parser'
const HOME_DESCRIPTION =
  'Receive email as a webhook. Inbox Bridge forwards inbound email to your API as parsed JSON with automatic retries and one-click replay.'
const HOME_IMAGE = `${siteConfig.siteUrl}${siteConfig.siteImage}`

export const meta: MetaFunction = () => {
  return [
    { title: HOME_TITLE },
    { name: 'description', content: HOME_DESCRIPTION },
    { tagName: 'link', rel: 'canonical', href: siteConfig.siteUrl },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: siteConfig.siteUrl },
    { property: 'og:title', content: HOME_TITLE },
    { property: 'og:description', content: HOME_DESCRIPTION },
    { property: 'og:image', content: HOME_IMAGE },
    { property: 'og:site_name', content: siteConfig.siteTitle },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: HOME_TITLE },
    { name: 'twitter:description', content: HOME_DESCRIPTION },
    { name: 'twitter:image', content: HOME_IMAGE },
    {
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Inbox Bridge',
        description: HOME_DESCRIPTION,
        url: siteConfig.siteUrl,
        image: HOME_IMAGE,
        applicationCategory: 'DeveloperApplication',
        operatingSystem: 'Web',
        offers: [
          { '@type': 'Offer', name: 'Free', price: String(getPlanPriceUsd(PLANS.FREE, INTERVALS.MONTH)), priceCurrency: 'USD' },
          { '@type': 'Offer', name: 'Starter', price: String(getPlanPriceUsd(PLANS.STARTER, INTERVALS.MONTH)), priceCurrency: 'USD' },
          { '@type': 'Offer', name: 'Pro', price: String(getPlanPriceUsd(PLANS.PRO, INTERVALS.MONTH)), priceCurrency: 'USD' },
          { '@type': 'Offer', name: 'Business', price: String(getPlanPriceUsd(PLANS.BUSINESS, INTERVALS.MONTH)), priceCurrency: 'USD' },
        ],
        publisher: {
          '@type': 'Organization',
          name: siteConfig.siteTitle,
          url: siteConfig.siteUrl,
        },
      },
    },
    { 'script:ld+json': FAQ_JSON_LD },
  ]
}

export async function loader({ request }: LoaderFunctionArgs) {
  const sessionUser = await authenticator.isAuthenticated(request)

  let currentPlanId: string | null = null
  if (sessionUser?.id) {
    const subscription = await prisma.subscription.findUnique({
      where: { userId: sessionUser.id },
    })
    currentPlanId = subscription?.planId ?? PLANS.FREE
  }

  return { user: sessionUser, currentPlanId }
}

export async function action({ request }: ActionFunctionArgs) {
  const sessionUser = await authenticator.isAuthenticated(request)
  if (!sessionUser?.id) {
    return redirect(LOGIN_PATH)
  }

  const formData = await request.formData()
  const intent = formData.get(INTENTS.INTENT)

  if (intent === INTENTS.SUBSCRIPTION_CREATE_CHECKOUT) {
    const planId = String(formData.get('planId'))
    const planInterval = String(formData.get('planInterval'))
    const checkoutUrl = await createSubscriptionCheckout({
      userId: sessionUser.id,
      planId,
      planInterval,
      request,
    })
    if (!checkoutUrl) {
      return { success: false }
    }
    return redirect(checkoutUrl)
  }

  return {}
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

type PlanCard = {
  id: string
  name: string
  description: string
  monthlyPrice: number
  yearlyPrice: number
  popular?: boolean
  features: string[]
}

const PLAN_CARDS: PlanCard[] = [
  {
    id: PLANS.FREE,
    name: 'Free',
    description: 'Start with the basics, upgrade anytime.',
    monthlyPrice: getPlanPriceUsd(PLANS.FREE, INTERVALS.MONTH),
    yearlyPrice: getPlanPriceUsd(PLANS.FREE, INTERVALS.YEAR),
    features: [
      '1 bridge',
      '1,000 emails/month',
      '3 retries',
      'No credit card required',
      'Upgrade anytime',
    ],
  },
  {
    id: PLANS.STARTER,
    name: 'Starter',
    description: 'Run your first production email-to-webhook workflows.',
    monthlyPrice: getPlanPriceUsd(PLANS.STARTER, INTERVALS.MONTH),
    yearlyPrice: getPlanPriceUsd(PLANS.STARTER, INTERVALS.YEAR),
    features: [
      '5 bridges',
      '5,000 emails/month',
      '5 retries',
      'Manual replay',
      'JSON viewer',
      'Attachment downloads (up to 35MB)',
    ],
  },
  {
    id: PLANS.PRO,
    name: 'Pro',
    description: 'For apps that rely on email as a data source.',
    monthlyPrice: getPlanPriceUsd(PLANS.PRO, INTERVALS.MONTH),
    yearlyPrice: getPlanPriceUsd(PLANS.PRO, INTERVALS.YEAR),
    popular: true,
    features: [
      '15 bridges',
      '10,000 emails/month',
      '10 retries',
      'Everything in Starter',
      'Priority support',
    ],
  },
  {
    id: PLANS.BUSINESS,
    name: 'Business',
    description: 'High-volume pipelines and critical workflows.',
    monthlyPrice: getPlanPriceUsd(PLANS.BUSINESS, INTERVALS.MONTH),
    yearlyPrice: getPlanPriceUsd(PLANS.BUSINESS, INTERVALS.YEAR),
    features: ['50+ bridges', '50,000 emails/month', '15 retries', 'Everything in Pro'],
  },
]

const HOW_IT_WORKS = [
  {
    step: '1',
    title: 'Create a bridge',
    description:
      'Get a unique email address and configure your webhook endpoint in seconds.',
  },
  {
    step: '2',
    title: 'Receive emails',
    description:
      'Create a forwarding rule or use the address directly. We handle all inbound processing.',
  },
  {
    step: '3',
    title: 'Get webhooks',
    description:
      'Structured JSON payloads hit your endpoint instantly with automatic retries on failure.',
  },
]

const FEATURES = [
  {
    icon: Zap,
    title: 'Instant delivery',
    description:
      'Emails are parsed and forwarded to your webhook endpoint in seconds, not minutes.',
  },
  {
    icon: RefreshCw,
    title: 'Automatic retries',
    description:
      'Failed deliveries are retried with exponential backoff. Never miss a payload.',
  },
  {
    icon: Shield,
    title: 'Secure by default',
    description:
      'Payloads are encrypted in transit. Hash-based email addresses keep your endpoints private.',
  },
  {
    icon: Code2,
    title: 'Structured JSON',
    description:
      'Every email is parsed into a clean JSON payload — subject, body, headers, attachments, and metadata.',
  },
  {
    icon: BarChart3,
    title: 'Tracking & replay',
    description:
      'See the status, response code, and duration of every delivery attempt — and replay any delivery with one click.',
  },
  {
    icon: Paperclip,
    title: 'Attachments included',
    description:
      'Files up to 35MB are stored encrypted and delivered as secure, expiring download links — no giant base64 blobs hitting your endpoint.',
  },
]

const FAQS: { question: string; answer: string }[] = [
  {
    question: 'How is this different from SendGrid Inbound Parse or Mailgun Routes?',
    answer:
      'Inbox Bridge focuses on one job — turning inbound email into a reliable JSON webhook — and gives you a dashboard, retries with exponential backoff, one-click delivery replay, and per-bridge delivery logs out of the box. SendGrid and Mailgun bundle inbound parsing inside large email platforms; for a small team that just needs the webhook, the surface area and pricing here are simpler.',
  },
  {
    question: 'Do I get a custom domain, or just @webhook.inboxbridge.io?',
    answer:
      'Every bridge gets a unique hash-based address at webhook.inboxbridge.io (for example a1b2c3d4@webhook.inboxbridge.io). Custom domains are not part of the current plans — most teams either point a forwarding rule at the bridge address or use the address directly.',
  },
  {
    question: 'What happens if my webhook endpoint is down?',
    answer:
      'A non-2xx response moves the delivery into a retry queue. Inbox Bridge retries with exponential backoff (30s, 60s, 2m, and so on, with 20% jitter) up to your plan’s retry limit. You can also manually replay any delivery from the bridge detail page once your endpoint is healthy.',
  },
  {
    question: 'How are attachments delivered?',
    answer:
      'Every attachment arrives in the JSON payload as an object with its filename, content type, and size. On paid plans we also store the file and add a secure, time-limited downloadUrl — so you pull the bytes only when you need them instead of bloating every webhook. Free bridges receive attachment metadata only.',
  },
  {
    question: 'Is there a free tier?',
    answer:
      'Yes. The Free plan covers 1 bridge, 1,000 emails per month, and 3 retry attempts. No credit card required — sign up and you can be receiving webhooks in a couple of minutes.',
  },
  {
    question: 'How quickly does an email become a webhook delivery?',
    answer:
      'Typically a few seconds end to end. Postmark accepts the inbound email, Inbox Bridge parses it and POSTs the JSON payload to your endpoint immediately. There is no polling delay.',
  },
]

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Index() {
  const { user, currentPlanId } = useLoaderData<typeof loader>()
  const theme = useTheme()
  const [isAnnual, setIsAnnual] = useState(false)

  return (
    <>
      {/* Content */}
      <div className="z-10 mx-auto flex w-full max-w-screen-lg flex-col px-6">
        {/* ---------------------------------------------------------------- */}
        {/* Hero                                                             */}
        {/* ---------------------------------------------------------------- */}
        <section className="flex flex-col items-center justify-center gap-6 pb-16 pt-16 md:pb-24 md:pt-28">
          <h1 className="max-w-3xl text-center text-5xl font-bold leading-[1.1] tracking-tight text-primary md:text-6xl lg:text-7xl">
            Turn any email into a webhook
          </h1>
          <p className="max-w-2xl text-center text-lg leading-relaxed text-muted-foreground md:text-xl">
            Inbox Bridge gives you a unique email address that instantly forwards every
            message to your API as a structured JSON payload. No email servers. No
            polling. Just webhooks.
          </p>
          <div className="mt-2 flex items-center gap-3">
            <Link
              to={LOGIN_PATH}
              tabIndex={-1}
              onClick={() =>
                track('Homepage CTA Clicked', {
                  location: 'hero',
                  authenticated: !!user,
                })
              }
              className="outline-none">
              <button
                className={cn(
                  buttonVariants({ variant: 'default', size: 'lg' }),
                  'gap-2 font-semibold',
                )}>
                Sign up for free
                <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
            <a
              href="#how-it-works"
              onClick={() =>
                track('Homepage CTA Clicked', { location: 'how_it_works_anchor' })
              }
              className={cn(buttonVariants({ variant: 'outline', size: 'lg' }))}>
              See how it works
            </a>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Free forever on the starter tier. No credit card required.
          </p>
          <p className="text-xs text-muted-foreground">
            Migrating?{' '}
            <Link
              to="/cloudmailin-alternative"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Compare to CloudMailin
            </Link>
            .
          </p>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Live demo table                                                  */}
        {/* ---------------------------------------------------------------- */}
        <section className="flex flex-col items-center pb-24">
          <DemoDeliveryTable />
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* How it works                                                     */}
        {/* ---------------------------------------------------------------- */}
        <section
          id="how-it-works"
          className="flex flex-col items-center gap-12 pb-24 pt-8">
          <div className="flex flex-col items-center gap-2">
            <h2 className="text-center text-3xl font-bold text-primary md:text-4xl">
              How Inbox Bridge works
            </h2>
            <p className="max-w-lg text-center text-muted-foreground">
              Go from zero to receiving webhook payloads in under a minute.
            </p>
          </div>

          <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-3">
            {HOW_IT_WORKS.map((item) => (
              <div
                key={item.step}
                className="flex flex-col items-center gap-3 text-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-primary">{item.title}</h3>
                <p className="max-w-xs text-base leading-relaxed text-muted-foreground">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          {/* Visual: email → bridge → webhook */}
          <div className="flex w-full max-w-md items-center justify-center gap-3 rounded-lg border border-border bg-secondary/50 px-6 py-4">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Mail className="h-5 w-5" />
              <span>Email</span>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground/60" />
            <div className="flex items-center gap-2 text-sm font-bold text-primary">
              <Logo width={20} height={20} />
              <span>Bridge</span>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground/60" />
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Code2 className="h-5 w-5" />
              <span>Webhook</span>
            </div>
          </div>

          <Link
            to="/guides/inbound-email-webhook"
            prefetch="intent"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary underline underline-offset-4 hover:no-underline">
            Read the full inbound email webhook tutorial
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* What you'll receive                                              */}
        {/* ---------------------------------------------------------------- */}
        <section className="flex flex-col items-center gap-8 pb-24">
          <div className="flex flex-col items-center gap-2">
            <h2 className="text-center text-3xl font-bold text-primary md:text-4xl">
              Example webhook payload
            </h2>
            <p className="max-w-lg text-center text-muted-foreground">
              A clean JSON payload posted to your webhook for every inbound email.
            </p>
          </div>
          <pre className="w-full max-w-2xl overflow-x-auto rounded-lg border border-border bg-secondary/50 p-5 text-sm leading-relaxed">
            <code className="font-mono text-primary">
              <span className="text-muted-foreground">{'{'}</span>
              {'\n  '}
              <span className="text-primary/80">&quot;id&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-emerald-600 dark:text-emerald-400">
                &quot;clx9k2p7a0001abcd&quot;
              </span>
              <span className="text-muted-foreground">,</span>
              {'\n  '}
              <span className="text-primary/80">&quot;receivedAt&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-emerald-600 dark:text-emerald-400">
                &quot;2026-05-12T14:23:01.000Z&quot;
              </span>
              <span className="text-muted-foreground">,</span>
              {'\n  '}
              <span className="text-primary/80">&quot;from&quot;</span>
              <span className="text-muted-foreground">: {'{ '}</span>
              <span className="text-primary/80">&quot;email&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-emerald-600 dark:text-emerald-400">
                &quot;stripe@stripe.com&quot;
              </span>
              <span className="text-muted-foreground">, </span>
              <span className="text-primary/80">&quot;name&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-emerald-600 dark:text-emerald-400">
                &quot;Stripe&quot;
              </span>
              <span className="text-muted-foreground">{' }'},</span>
              {'\n  '}
              <span className="text-primary/80">&quot;to&quot;</span>
              <span className="text-muted-foreground">: [{'{ '}</span>
              <span className="text-primary/80">&quot;email&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-emerald-600 dark:text-emerald-400">
                &quot;a1b2c3d4@webhook.inboxbridge.io&quot;
              </span>
              <span className="text-muted-foreground">, </span>
              <span className="text-primary/80">&quot;name&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-emerald-600 dark:text-emerald-400">&quot;&quot;</span>
              <span className="text-muted-foreground">{' }'}],</span>
              {'\n  '}
              <span className="text-primary/80">&quot;cc&quot;</span>
              <span className="text-muted-foreground">: [],</span>
              {'\n  '}
              <span className="text-primary/80">&quot;bcc&quot;</span>
              <span className="text-muted-foreground">: [],</span>
              {'\n  '}
              <span className="text-primary/80">&quot;replyTo&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-sky-600 dark:text-sky-400">null</span>
              <span className="text-muted-foreground">,</span>
              {'\n  '}
              <span className="text-primary/80">&quot;subject&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-emerald-600 dark:text-emerald-400">
                &quot;Payment received for invoice INV-4829&quot;
              </span>
              <span className="text-muted-foreground">,</span>
              {'\n  '}
              <span className="text-primary/80">&quot;date&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-emerald-600 dark:text-emerald-400">
                &quot;2026-05-12T14:23:00.000Z&quot;
              </span>
              <span className="text-muted-foreground">,</span>
              {'\n  '}
              <span className="text-primary/80">&quot;textBody&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-emerald-600 dark:text-emerald-400">
                &quot;Your payment of $249.00 was successful...&quot;
              </span>
              <span className="text-muted-foreground">,</span>
              {'\n  '}
              <span className="text-primary/80">&quot;htmlBody&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-emerald-600 dark:text-emerald-400">
                &quot;&lt;p&gt;Your payment of $249.00 was successful...&lt;/p&gt;&quot;
              </span>
              <span className="text-muted-foreground">,</span>
              {'\n  '}
              <span className="text-primary/80">&quot;attachments&quot;</span>
              <span className="text-muted-foreground">: [</span>
              {'\n    '}
              <span className="text-muted-foreground">{'{'}</span>
              {'\n      '}
              <span className="text-primary/80">&quot;name&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-emerald-600 dark:text-emerald-400">
                &quot;receipt.pdf&quot;
              </span>
              <span className="text-muted-foreground">,</span>
              {'\n      '}
              <span className="text-primary/80">&quot;contentType&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-emerald-600 dark:text-emerald-400">
                &quot;application/pdf&quot;
              </span>
              <span className="text-muted-foreground">,</span>
              {'\n      '}
              <span className="text-primary/80">&quot;contentLength&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-amber-600 dark:text-amber-400">18244</span>
              <span className="text-muted-foreground">,</span>
              {'\n      '}
              <span className="text-primary/80">&quot;contentId&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-sky-600 dark:text-sky-400">null</span>
              <span className="text-muted-foreground">,</span>
              {'\n      '}
              <span className="text-primary/80">&quot;downloadUrl&quot;</span>
              <span className="text-muted-foreground">: </span>
              <span className="text-emerald-600 dark:text-emerald-400">
                &quot;https://inboxbridge.io/attachments/clx1a2b3?token=…&quot;
              </span>
              {'\n    '}
              <span className="text-muted-foreground">{'}'}</span>
              {'\n  '}
              <span className="text-muted-foreground">]</span>
              {'\n'}
              <span className="text-muted-foreground">{'}'}</span>
            </code>
          </pre>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Features                                                         */}
        {/* ---------------------------------------------------------------- */}
        <section className="flex flex-col items-center gap-12 pb-24">
          <div className="flex flex-col items-center gap-2">
            <h2 className="text-center text-3xl font-bold text-primary md:text-4xl">
              Built for reliability
            </h2>
            <p className="max-w-lg text-center text-muted-foreground">
              Everything you need to pipe email data into your application with
              confidence.
            </p>
          </div>

          <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature) => (
              <Card key={feature.title} className="border-border/60">
                <CardHeader>
                  <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-secondary">
                    <feature.icon className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-base">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-base leading-relaxed text-muted-foreground">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground">
            New to this?{' '}
            <Link
              to="/email-to-webhook"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Learn how email-to-webhook works
            </Link>
            .
          </p>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Use cases                                                        */}
        {/* ---------------------------------------------------------------- */}
        <section className="flex flex-col items-center gap-12 pb-24">
          <div className="flex flex-col items-center gap-2">
            <h2 className="text-center text-3xl font-bold text-primary md:text-4xl">
              Use cases
            </h2>
            <p className="max-w-lg text-center text-muted-foreground">
              Anywhere email meets code, Inbox Bridge fits.
            </p>
          </div>

          <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
            {[
              {
                title: 'Inbound lead capture',
                description:
                  'Forward form submission emails to your CRM or database via webhook. No parsing scripts needed.',
              },
              {
                title: 'Transactional email monitoring',
                description:
                  'Track delivery receipts, bounce notifications, and out-of-office replies in real time.',
              },
              {
                title: 'Support ticket ingestion',
                description:
                  'Pipe customer emails directly into your ticketing system or Slack channel.',
              },
              {
                title: 'IoT and device alerts',
                description:
                  'Machines and sensors that email alerts? Turn them into structured data your backend can act on.',
              },
              {
                title: 'Invoice and document processing',
                description:
                  'PDFs, receipts, and reports arrive as secure download links — feed them straight into your parser or approval workflow.',
              },
              {
                title: 'AI agents that read email',
                description:
                  'Give your agent an inbox. Clean JSON plus attachment links, ready to drop into an LLM pipeline — no IMAP required.',
              },
            ].map((useCase) => (
              <div key={useCase.title} className="rounded-lg border border-border/60 p-6">
                <h3 className="mb-2 text-lg font-semibold text-primary">
                  {useCase.title}
                </h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Pricing                                                          */}
        {/* ---------------------------------------------------------------- */}
        <section id="pricing" className="flex flex-col items-center gap-8 pb-24">
          <div className="flex flex-col items-center gap-4">
            <h2 className="text-center text-3xl font-bold text-primary md:text-4xl">
              Simple, transparent pricing
            </h2>
            <p className="text-center text-muted-foreground">
              Start free. Scale as you grow.
            </p>
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">Monthly</label>
              <Switch
                checked={isAnnual}
                onCheckedChange={(checked) => {
                  setIsAnnual(checked)
                  track('Homepage Pricing Interval Toggled', {
                    interval: checked ? INTERVALS.YEAR : INTERVALS.MONTH,
                  })
                }}
              />
              <label className="text-sm text-muted-foreground">
                Yearly{' '}
                <span className="text-xs font-medium text-primary">(save 20%)</span>
              </label>
            </div>
          </div>

          <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PLAN_CARDS.map((plan) => {
              const isCurrent = currentPlanId === plan.id
              const isFreePlan = plan.id === PLANS.FREE

              return (
                <Card
                  key={plan.name}
                  className={cn(
                    'relative flex flex-col',
                    plan.popular && 'border-primary shadow-md',
                  )}>
                  {plan.popular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                      Most Popular
                    </Badge>
                  )}
                  <CardHeader className={cn(plan.popular && 'pt-8')}>
                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col gap-4">
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-primary">
                          $
                          {isAnnual && plan.monthlyPrice > 0
                            ? plan.yearlyPrice.toFixed(2)
                            : plan.monthlyPrice}
                        </span>
                        <span className="text-sm text-muted-foreground">/month</span>
                      </div>
                      {isAnnual && plan.monthlyPrice > 0 && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Billed annually at ${(plan.yearlyPrice * 12).toFixed(2)}
                        </p>
                      )}
                    </div>
                    <ul className="flex flex-col gap-2">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2 text-sm">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardFooter>
                    {isCurrent ? (
                      <Button variant="outline" size="sm" className="w-full" disabled>
                        Current Plan
                      </Button>
                    ) : !user ? (
                      <Link
                        to={LOGIN_PATH}
                        tabIndex={-1}
                        onClick={() =>
                          track('Homepage Plan CTA Clicked', {
                            plan_id: plan.id,
                            interval: isAnnual ? INTERVALS.YEAR : INTERVALS.MONTH,
                            action: 'signup',
                            authenticated: false,
                          })
                        }
                        className="w-full outline-none">
                        <button
                          className={cn(
                            buttonVariants({
                              variant: plan.popular ? 'default' : 'outline',
                              size: 'sm',
                            }),
                            'w-full',
                            plan.popular && 'font-semibold shadow-sm',
                          )}>
                          Sign up for free
                        </button>
                      </Link>
                    ) : isFreePlan ? (
                      <Link
                        to="/dashboard"
                        tabIndex={-1}
                        onClick={() =>
                          track('Homepage Plan CTA Clicked', {
                            plan_id: plan.id,
                            action: 'dashboard',
                            authenticated: true,
                          })
                        }
                        className="w-full outline-none">
                        <button
                          className={cn(
                            buttonVariants({ variant: 'outline', size: 'sm' }),
                            'w-full',
                          )}>
                          Go to Dashboard
                        </button>
                      </Link>
                    ) : (
                      <Form method="POST" className="w-full">
                        <input type="hidden" name="planId" value={plan.id} />
                        <input
                          type="hidden"
                          name="planInterval"
                          value={isAnnual ? INTERVALS.YEAR : INTERVALS.MONTH}
                        />
                        <Button
                          type="submit"
                          variant={plan.popular ? 'default' : 'outline'}
                          size="sm"
                          name={INTENTS.INTENT}
                          value={INTENTS.SUBSCRIPTION_CREATE_CHECKOUT}
                          onClick={() =>
                            track('Homepage Plan CTA Clicked', {
                              plan_id: plan.id,
                              interval: isAnnual ? INTERVALS.YEAR : INTERVALS.MONTH,
                              action: 'upgrade',
                              authenticated: true,
                            })
                          }
                          className={cn(
                            'w-full',
                            plan.popular && 'font-semibold shadow-sm',
                          )}>
                          Upgrade to {plan.name}
                        </Button>
                      </Form>
                    )}
                  </CardFooter>
                </Card>
              )
            })}
          </div>

          <p className="text-center text-muted-foreground">
            Need something different?{' '}
            <a
              href={`mailto:${siteConfig.email}`}
              onClick={() => track('Homepage Contact Clicked', { source: 'pricing' })}
              className={cn(
                buttonVariants({ variant: 'link', size: 'sm' }),
                'h-auto p-0',
              )}>
              Email us
            </a>
          </p>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* FAQ                                                              */}
        {/* ---------------------------------------------------------------- */}
        <section className="flex flex-col items-center gap-8 pb-24">
          <div className="flex flex-col items-center gap-2">
            <h2 className="text-center text-3xl font-bold text-primary md:text-4xl">
              Frequently asked questions
            </h2>
          </div>
          <div className="flex w-full max-w-2xl flex-col gap-4">
            {FAQS.map((faq) => (
              <details
                key={faq.question}
                className="group rounded-lg border border-border/60 bg-secondary/30 p-5 open:bg-secondary/50">
                <summary className="flex cursor-pointer items-start justify-between gap-4 text-base font-medium text-primary">
                  <span>{faq.question}</span>
                  <span
                    aria-hidden="true"
                    className="mt-1 text-muted-foreground transition group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Final CTA                                                        */}
        {/* ---------------------------------------------------------------- */}
        <section className="flex flex-col items-center gap-6 pb-24">
          <h2 className="max-w-xl text-center text-3xl font-bold text-primary md:text-4xl">
            Stop building email infrastructure
          </h2>
          <p className="max-w-lg text-center text-muted-foreground">
            Let Inbox Bridge handle parsing, delivery, and retries so you can focus on
            what your app does with the data.
          </p>
          <Link
            to={LOGIN_PATH}
            tabIndex={-1}
            onClick={() =>
              track('Homepage CTA Clicked', {
                location: 'final',
                authenticated: !!user,
              })
            }
            className="outline-none">
            <button
              className={cn(
                buttonVariants({ variant: 'default', size: 'lg' }),
                'gap-2 font-semibold',
              )}>
              Sign up for free
              <ArrowRight className="h-4 w-4" />
            </button>
          </Link>
        </section>
      </div>

      {/* Background */}
      <img
        src={ShadowPNG}
        alt=""
        aria-hidden="true"
        width={4773}
        height={3600}
        loading="lazy"
        decoding="async"
        className={`fixed left-0 top-0 z-0 h-full w-full opacity-60 ${theme === 'dark' ? 'invert' : ''}`}
      />
      <div className="base-grid fixed h-screen w-screen opacity-40" />
      <div className="fixed bottom-0 h-screen w-screen bg-gradient-to-t from-[hsl(var(--card))] to-transparent" />
    </>
  )
}
